import { DefaultValues, Path, useForm } from 'react-hook-form';
import {
  ContentType,
  ContentTypeName,
  ValueByType,
} from '../model/extras.domain';
import {
  ErrorsVM,
  toErrors,
} from '@client/new/pages/promotions/view-model/newDiscount.mappers';
import { useRef, useState } from 'react';
import { Actions, Dispatchers } from '@client/new/helpers/types';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { ExtrasModel } from '../model/extras.model';
import { defaultValues } from './extras.mappers';
import { zodResolver } from '@hookform/resolvers/zod';
import { extrasFormSchemaMap, validateImage } from './extras.validation';
import { reset } from 'mixpanel-browser';

export type ExtrasFormVMActionMap = {
  StartBannerUpload: null;
  RemoveBanner: null;
  UploadBanner: File;
  AddBanner: string;
};

type ExtrasFormVMActions = Actions<ExtrasFormVMActionMap>;

export type ExtrasFormVM<T extends ContentTypeName> = {
  reset: ReturnType<typeof useForm<ValueByType<T>>>['reset'];
  register: (key: Path<ValueByType<T>>) => {
    onChange: (e: React.ChangeEvent<any>) => void;
    value: any;
    error?: string;
  } & ReturnType<typeof useForm<ValueByType<T>>>['register'];
  errors: ErrorsVM | null;
  isUploading: boolean;
  uploadInputRef: React.MutableRefObject<HTMLInputElement | null>;
  values: ValueByType<T>;
  onSubmit: () => void;
} & Dispatchers<ExtrasFormVMActionMap>;

// export type ExtrasFormVM<T extends ContentTypeName> = typeof useExtrasFormVM;

type ExtrasFormVMParams<T extends ContentTypeName> = {
  formType: T;
  model: ExtrasModel;
  handleSubmit: (data: ContentType) => Promise<unknown>;
};

export const useExtrasFormVM = <T extends ContentTypeName>(
  p: ExtrasFormVMParams<T>,
) => {
  const { formType, model } = p;
  const [inProgress, setInProgress] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<ValueByType<T>>({
    reValidateMode: 'onChange',
    defaultValues: defaultValues[formType] as DefaultValues<ValueByType<T>>,
    resolver: zodResolver(extrasFormSchemaMap[formType]),
  });

  const values = form.watch();

  const register = (key: Path<ValueByType<T>>) => {
    const { onChange: orgOnChange, ...orgRegister } = form.register(key);
    const formState = form.formState;
    const { setValue } = form;
    const error = form.getFieldState(key, formState)?.error?.message;
    const value = form.getValues(key);

    const onChange = (e: React.ChangeEvent<any>) => {
      setValue(key, e.target.value, { shouldValidate: true });
      return orgOnChange(e);
    };

    return {
      ...orgRegister,
      error,
      value,
      onChange,
    };
  };

  const onSubmit = (value: ValueByType<T>) => {
    setInProgress(true);

    p.handleSubmit({
      type: formType as T,
      value,
    } as ContentType)
      .then(() => {
        setShowErrors(false);
      })
      .catch((error) => {
        console.error('Error submitting form:', error);
        setShowErrors(true);
      })
      .finally(() => {
        setInProgress(false);
      });
  };

  const _dispatch = (a: ExtrasFormVMActions) => {
    trackSessionEvent('ViewAction', a);
    switch (a.type) {
      case 'UploadBanner': {
        if (isUploading) return;
        setIsUploading(true);

        // @ts-expect-error todo: fix this type
        form.clearErrors('coverImage');

        validateImage(a.payload).then((p) => {
          console.log('Image validation result:', p);
          if (!p.isValid) {
            // @ts-expect-error todo: fix this type

            form.setError('coverImage', {
              type: 'manual',
              message: p.error || 'Invalid image format or size.',
            });
            setIsUploading(false);
            return;
          }

          model
            .uploadFile(a.payload)
            .then((url) => dispatchers.dispatch('AddBanner', url))
            .finally(() => setIsUploading(false));
        });

        return;
      }
      case 'AddBanner': {
        if (formType !== 'ContentLink') return;
        // @ts-expect-error todo: fix this type
        form.setValue('coverImage', a.payload, { shouldValidate: true });
        return;
      }
      case 'StartBannerUpload': {
        if (!uploadInputRef.current) return;
        uploadInputRef.current.click();
      }
      case 'RemoveBanner': {
        if (formType !== 'ContentLink') return;
        // @ts-expect-error todo: fix this type
        form.setValue('coverImage', '', { shouldValidate: false });
        return;
      }
    }
  };

  const dispatchers = getDispatchers<ExtrasFormVMActionMap>(_dispatch);

  return {
    register,
    reset: form.reset,
    onSubmit: () => {
      if (inProgress) return;
      setShowErrors(true);
      form.handleSubmit(onSubmit)();
    },
    errors: showErrors ? toErrors(form.formState.errors) : null,
    isUploading,
    uploadInputRef,
    ...dispatchers,
    values,
  };
};
